import { useEffect, useRef, useState } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { Download, FileText, CheckCircle2, Clock, XCircle, Users, BarChart3 } from 'lucide-react';

const STATUS_STYLES = {
  completed:   { bg: 'bg-emerald-50',  fg: 'text-emerald-700',  label: 'Completed',   Icon: CheckCircle2 },
  in_progress: { bg: 'bg-blue-50',     fg: 'text-blue-700',     label: 'In progress', Icon: Clock },
  failed:      { bg: 'bg-rose-50',     fg: 'text-rose-700',     label: 'Failed',      Icon: XCircle },
  not_started: { bg: 'bg-slate-50',    fg: 'text-slate-600',    label: 'Not started', Icon: Users },
};

const fmtDate = (s) => { if (!s) return '—'; try { return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return s; } };

/**
 * Per-module training progress dialog.
 *
 * Props:
 *   - moduleId, moduleTitle — which module to load
 *   - open, onOpenChange — Dialog control
 *
 * Loads /api/training/modules/{mid}/report on open. "Download PDF" hits the .pdf variant.
 */
export default function TrainingModuleReportDialog({ moduleId, moduleTitle, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !moduleId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/training/modules/${moduleId}/report`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) toast.error('Could not load report');
      }
    })();
    return () => { cancelled = true; };
  }, [open, moduleId]);

  // Reset cached data whenever a different module is selected.
  const prevModuleId = useRef(null);
  useEffect(() => {
    if (prevModuleId.current !== moduleId) {
      prevModuleId.current = moduleId;
      setData(null);
    }
  }, [moduleId]);

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const { data: blob } = await api.get(`/training/modules/${moduleId}/report.pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `training-report-${(moduleTitle || moduleId).replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 1000);
    } catch (e) {
      toast.error('Could not download PDF');
    } finally {
      setBusy(false);
    }
  };

  const summary = data?.summary;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="module-report-dialog" className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 flex items-center gap-2">
                <BarChart3 className="w-3.5 h-3.5" /> Progress report
              </div>
              <DialogTitle className="font-heading text-2xl mt-1">{moduleTitle}</DialogTitle>
            </div>
          </div>
        </DialogHeader>

        {!data && <div className="text-center py-12 text-slate-500" data-testid="module-report-loading">Loading report…</div>}

        {data && (
          <>
            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5 mt-3">
              <KPI label="Assigned"    value={summary.total}         color="bg-slate-50 text-slate-700" testid="report-total" />
              <KPI label="Completed"   value={summary.completed}     color="bg-emerald-50 text-emerald-700" testid="report-completed" />
              <KPI label="In progress" value={summary.in_progress}   color="bg-blue-50 text-blue-700" testid="report-in-progress" />
              <KPI label="Not started" value={summary.not_started}   color="bg-slate-100 text-slate-700" testid="report-not-started" />
              <KPI label="Completion"  value={`${summary.completion_rate}%`} color="bg-indigo-50 text-indigo-700" testid="report-completion-rate" />
            </div>

            {/* Module meta */}
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <Meta label="Audience" value={
                data.module.scope === 'all' ? 'All active employees' :
                data.module.scope === 'departments' ? `Departments: ${(data.module.assignee_departments || []).join(', ') || '—'}` :
                'Specific employees'
              } />
              <Meta label="Mandatory" value={data.module.mandatory ? 'Yes' : 'Optional'} />
              <Meta label="Deadline" value={fmtDate(data.module.deadline)} />
              <Meta label="Created by" value={data.module.created_by || '—'} />
            </div>

            {/* Rows */}
            <div className="mt-4 border border-slate-200 rounded-xl overflow-hidden" data-testid="report-table">
              <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 bg-slate-50 px-4 py-2 border-b border-slate-200">
                <div className="col-span-1">EMP ID</div>
                <div className="col-span-3">Name</div>
                <div className="col-span-2">Department</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-1 text-right">Score</div>
                <div className="col-span-1 text-right">Tries</div>
                <div className="col-span-2">Completed</div>
              </div>
              <div className="max-h-[44vh] overflow-y-auto">
                {data.rows.map(r => {
                  const s = STATUS_STYLES[r.status] || STATUS_STYLES.not_started;
                  return (
                    <div key={r.user_id} data-testid={`report-row-${r.user_id}`}
                         className="grid grid-cols-12 gap-2 text-xs px-4 py-2.5 border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                      <div className="col-span-1 font-mono text-[10px] text-slate-500">{r.employee_id || '—'}</div>
                      <div className="col-span-3">
                        <div className="font-semibold text-slate-900 truncate">{r.name}</div>
                        <div className="text-[10px] text-slate-500 truncate">{r.title || ''}</div>
                      </div>
                      <div className="col-span-2 text-slate-600 truncate">{r.department || '—'}</div>
                      <div className="col-span-2">
                        <span className={`inline-flex items-center gap-1 ${s.bg} ${s.fg} text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full`}>
                          <s.Icon className="w-3 h-3" strokeWidth={2.2} /> {s.label}
                        </span>
                      </div>
                      <div className="col-span-1 text-right font-mono text-slate-700">{r.score != null ? r.score : '—'}</div>
                      <div className="col-span-1 text-right font-mono text-slate-500">{r.attempts || 0}</div>
                      <div className="col-span-2 text-slate-500">{fmtDate(r.completed_at)}</div>
                    </div>
                  );
                })}
                {data.rows.length === 0 && (
                  <div className="text-center py-12 text-slate-400 text-sm">No employees are assigned to this module yet.</div>
                )}
              </div>
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="report-close">Close</Button>
          <Button onClick={downloadPdf} disabled={!data || busy}
                  className="rounded-full bg-[#3D4A8E] hover:bg-[#2F3A6E]" data-testid="report-download-pdf">
            <Download className="w-4 h-4 mr-2" strokeWidth={1.8} /> {busy ? 'Downloading…' : 'Download PDF'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const KPI = ({ label, value, color, testid }) => (
  <div className={`rounded-xl px-3 py-2 ${color}`} data-testid={testid}>
    <div className="text-[10px] font-bold uppercase tracking-[0.15em] opacity-75">{label}</div>
    <div className="font-heading text-2xl font-bold mt-0.5">{value}</div>
  </div>
);

const Meta = ({ label, value }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">{label}</div>
    <div className="text-slate-800 font-medium mt-0.5">{value}</div>
  </div>
);
